import {DateTime} from 'luxon';
import {ClinchCondition} from '../conditions/conditions.types';
import {DropCondition} from '../optimization/optimization.types';
import {CampaignUsage} from '../campaigns/campaigns.types';
import {RetrievalTypeValue} from '../enums/static/RetrievalType';
import {CompoundCondition} from '../compoundConditions/compoundConditions.types';

export enum FeedRelatedItemRelationType {
  ProductSet = 'ProductSet',
  TopItems = 'TopItems',
  FieldToItems = 'FieldToItems',
}

enum RelationType {
  ItemToItems = 0,
  BrandToItems = 20,
  ProductSet = 200,
  TopItems = 100,
  CategoryTopItems = 10,
  Custom0ToItems = 300,
  Custom1ToItems = 301,
  Custom2ToItems = 302,
  Custom3ToItems = 303,
  Custom4ToItems = 304,
  Custom5ToItems = 305,
  Custom6ToItems = 306,
  Custom7ToItems = 307,
  Custom8ToItems = 308,
  Custom9ToItems = 309,
  Custom10ToItems = 310,
  Custom11ToItems = 311,
  Custom12ToItems = 312,
  Custom13ToItems = 313,
  Custom14ToItems = 314,
  Custom15ToItems = 315,
  Custom16ToItems = 316,
  Custom17ToItems = 317,
  Custom18ToItems = 318,
  Custom19ToItems = 319,
}

const RelationTypeToName: Partial<Record<RelationType, string>> = {
  [RelationType.ProductSet]: 'Product Set',
  [RelationType.TopItems]: 'Top Items',
  [RelationType.CategoryTopItems]: 'Category Top Items',
};

enum FeedColumn {
  id = 1,
  category = 10,
  brand = 20,
  title = 30,
  price = 40,
  score = 50,
  custom0 = 100,
  custom1 = 110,
  custom2 = 120,
  custom3 = 130,
  custom4 = 204,
  custom5 = 205,
  custom6 = 206,
  custom7 = 207,
  custom8 = 208,
  custom9 = 209,
  custom10 = 210,
  custom11 = 211,
  custom12 = 212,
  custom13 = 213,
  custom14 = 214,
  custom15 = 215,
  custom16 = 216,
  custom17 = 217,
  custom18 = 218,
  custom19 = 219,
}

enum RelatedItemsOptimizationType {
  ServeEvenly = 0,
  OngoingOptimization = 10,
}

enum RelatedItemsType {
  ProductSet = 'ProductSet',
  TopItems = 'TopItems',
  CategoryTopItems = 'CategoryToItems',
}

interface RelatedItemsOptimization {
  daysOfDataToTake: number;
  dropConditions: DropCondition[];
  itemsToShowGoal: number;
  optimizeBy: number;
  postOptimizationServingCoefficient: number;
  productsDiversityOption: number;
  selectOption: number;
}

interface RemoteRelatedItemsLogicsForFeed {
  id: number;
  feedId: number;
  items_count: number;
  name: string;
  relationType: RelationType;
  last_populated_date: string;
  usedInCampaigns: CampaignUsage[];
  optimization?: RelatedItemsOptimization;
  logicRule: AddRelatedItemLogicPayload;
}

interface RelatedItemsLogicsForFeed
  extends Omit<RemoteRelatedItemsLogicsForFeed, 'items_count' | 'last_populated_date'> {
  itemsCount: number;
  lastPopulatedDate: DateTime;
  maxItems?: number;
  videoAdsIds?: number[];
}

interface RelateItemMapping {
  label: string;
  value: string;
  isRequired: boolean;
}

interface AddRelatedItemLogicPayload {
  retrievalType: number;
  filtersIntersection: boolean;
  filtersTree: ClinchCondition;
  orderBy: string;
  orderDesc: boolean;
  manualItemsIds: string[];
  mapping: Record<string, string>;
  colNameMap: Record<string, string>;
  groupBy: FeedColumn[];
}

interface RemoteCategoryTopItem {
  itemsCount: number;
  key: string;
}

interface CategoryTopItem extends Omit<RemoteCategoryTopItem, 'key'> {
  category: string;
}

interface RemoteRelatedItemOptimizationStats {
  date: string;
  imps: number;
  newItems: number;
  newlyOptimized: number;
  optimizationRateDaily: number;
  optimizationRateOverall: number;
  overallCollectingData: number;
  overallOptimized: number;
  overallOptimizedDroppable: number;
  overallOptimizedDropped: number;
  overallOptimizedNonDroppable: number;
  overallOptimizedServing: number;
  removedItems: number;
  replacementRate: number;
  results: number;
  resultsRate: number;
  sameItems: number;
  stayedOptimized: number;
  totalItems: number;
}

interface RelatedItemOptimizationStats extends Omit<RemoteRelatedItemOptimizationStats, 'date'> {
  date: DateTime;
}

interface CategoryToItemsGroupingField {
  value: string;
  uiName: string;
  taxonomyKey: string;
}

interface CategoryTopItemCategoriesList {
  itemsCount: number;
  key: string;
}

export interface FeedRelatedItemMetadata {
  id: number;
  feedId: number;
  name: string;
  type: FeedRelatedItemRelationType;
  retrievalType: RetrievalTypeValue;
  maxItems: number;
  itemsCount: number;
  usedInCampaigns: CampaignUsage[];
  useSignals: boolean;
}

type ComplexOptimization = {
  daysOfDataToTake: number;
  dropConditions: DropCondition[];
  itemsToShowGoal: number;
  postOptimizationServingCoefficient: number;
  productsDiversityOption: number;
  //complex optimization serving
  selectOption: 10;
};
export type FeedRelatedItemOptimization = {optimizeBy: number} & (
  | ComplexOptimization
  | {
      //serve evenly
      selectOption: 0;
    }
);

export interface OldFeedRelatedItemCondition {
  filtersTree: ClinchCondition;
  orderBy: string;
  orderDesc: boolean;
  groupBy: FeedColumn[];
}

export enum RelatedItemOrderBy {
  Descending = 'Descending',
  Ascending = 'Ascending',
}

export interface FeedRelatedItemWithConditions extends FeedRelatedItemMetadata {
  manualItemsIds: string[];
  externalFileUrl: string;
  orderBySignalKey: string;
  orderDirection: RelatedItemOrderBy;
  groupBySignalKey: string;
  condition?: CompoundCondition;
  websiteEngagementDataLookbackWindowInDays?: number;
}

export enum RelatedItemsSignalsPurpose {
  FilterBy = 'FilterBy',
  OrderBy = 'OrderBy',
  GroupBy = 'GroupBy',
}

export {
  RelatedItemsType,
  RelationType,
  RelationTypeToName,
  RelateItemMapping,
  RelatedItemsLogicsForFeed,
  AddRelatedItemLogicPayload,
  RemoteCategoryTopItem,
  CategoryTopItem,
  RemoteRelatedItemsLogicsForFeed,
  RelatedItemsOptimizationType,
  RelatedItemsOptimization,
  RemoteRelatedItemOptimizationStats,
  RelatedItemOptimizationStats,
  CategoryToItemsGroupingField,
  CategoryTopItemCategoriesList,
  FeedColumn,
};
